import type { Theme } from '@mui/material';

export const REQUIRED_COLOR = '#c0392b';

export const getMaxLengthValidationText = (t: (key: string) => string, max: number) =>
	t('validations.errors.maxLength').replace('{0}', String(max));

export const getCreateFormHeaderTitleSx = (theme: Theme) => ({
	fontSize: '18px !important',
	fontWeight: 700,
	color: theme.palette.root.formTitle,
});

export const getCreateFormHeaderSubtitleSx = (theme: Theme) => ({
	fontSize: '12.5px !important',
	lineHeight: 1.5,
	color: theme.palette.root.muted,
});

export const getFormHintSx = (theme: Theme) => ({
	fontSize: '11.5px !important',
	lineHeight: 1.55,
	color: theme.palette.root.border_teal,
});

export const getValidationHelperSx = () => ({
	fontSize: '11.5px !important',
	fontWeight: 400,
	lineHeight: 1.55,
});

export const getAudienceTitleSx = (theme: Theme) => ({
	fontSize: '13.5px !important',
	fontWeight: 600,
	color: theme.palette.root.formTitle,
});

export const getAudienceDescSx = (theme: Theme) => ({
	fontSize: '12px !important',
	lineHeight: 1.55,
	color: theme.palette.root.muted,
});

export const getFormInputSx = (theme: Theme) => {
	const { mediumGreen, inputFocusGreen, basicWhite } = theme.palette.root;
	const errorColor = theme.palette.error.main;
	const validationHelperSx = getValidationHelperSx();

	return {
		'& .MuiOutlinedInput-root': {
			height: 44,
			borderRadius: '4px',
			backgroundColor: '#fafcfc',
			fontSize: '14px',
			'&.Mui-focused': { backgroundColor: basicWhite },
			'& fieldset': { borderColor: mediumGreen },
			'&:hover fieldset': { borderColor: inputFocusGreen },
			'&.Mui-focused fieldset': { borderColor: inputFocusGreen, borderWidth: '1px' },
			'&.Mui-error fieldset': { borderColor: errorColor },
			'&.Mui-error:hover fieldset': { borderColor: errorColor },
			'&.Mui-error.Mui-focused fieldset': { borderColor: errorColor },
		},
		'& .MuiFormHelperText-root': validationHelperSx,
	};
};

export const getRichTextWrapperSx = (theme: Theme, hasError: boolean) => {
	const { formTitle, border_teal, mediumGreen, inputFocusGreen, basicWhite } = theme.palette.root;
	const errorColor = theme.palette.error.main;

	return {
		'--sm-link-label': '"הוסף קישור"',
		'--sm-unlink-label': '"הסר קישור"',
		'&[dir="ltr"]': { '--sm-link-label': '"Add Link"', '--sm-unlink-label': '"Remove Link"' },
		'& .rdw-editor-wrapper': { marginTop: 0 },
		'& .rdw-editor-toolbar': {
			margin: 0,
			padding: '6px 10px',
			gap: '4px',
			background: '#fafcfc',
			border: `1px solid ${hasError ? errorColor : mediumGreen}`,
			borderBottom: 'none',
			borderRadius: '4px 4px 0 0',
		},
		'& .rdw-inline-wrapper, & .rdw-link-wrapper': { margin: 0, gap: '4px', flexWrap: 'nowrap' },
		'& .rdw-option-wrapper': {
			minWidth: '30px',
			height: '28px',
			padding: '4px 8px',
			margin: 0,
			border: `1px solid ${mediumGreen}`,
			borderRadius: '4px',
			background: basicWhite,
			fontSize: '14px',
			color: formTitle,
			'&:hover': { boxShadow: 'none', background: '#f0f4f5' },
		},
		'& .rdw-option-active': { boxShadow: 'none', background: '#f0f4f5', borderColor: inputFocusGreen },
		'& .rdw-link-wrapper': { marginRight: '4px', paddingRight: '8px', borderRight: `1px solid ${mediumGreen}` },
		'& .rdw-link-wrapper .rdw-option-wrapper': {
			width: 'auto',
			gap: '6px',
			padding: '4px 10px',
			fontSize: '12.5px',
			fontWeight: 500,
		},
		'& .rdw-option-wrapper[title="Link"]::after': { content: 'var(--sm-link-label)' },
		'& .rdw-option-wrapper[title="Unlink"]': { color: REQUIRED_COLOR },
		'& .rdw-option-wrapper[title="Unlink"]::after': { content: 'var(--sm-unlink-label)' },
		'& .rdw-link-modal-target-option': { display: 'none' },
		'& .rdw-editor-main': {
			height: 'auto !important',
			minHeight: '104px !important',
			maxHeight: '320px',
			overflowY: 'auto',
			border: `1px solid ${hasError ? errorColor : mediumGreen} !important`,
			borderTop: 'none !important',
			borderRadius: '0 0 4px 4px !important',
			background: '#fafcfc',
			padding: '16px !important',
			lineHeight: 1.7,
			fontSize: '14px',
		},
		'& .public-DraftEditorPlaceholder-root': { color: border_teal },
	};
};

export const getCreateFormContainerSx = (theme: Theme) => ({
	width: '720px',
	maxWidth: '100%',
	mx: 'auto',
	'& .MuiCard-root': {
		width: '100%',
		border: '1px solid #fff',
		boxShadow: '0px 0px 1px rgba(0,58,53,0.2), 4px 4px 8px rgba(0,58,53,0.2)',
		transition: '.3s ease',
		'&:hover': { borderColor: theme.palette.root.mediumGreen },
	},
});
